import * as yup from 'yup';
import type { CustomerCreatePayload } from '@/api/customers';
import { DEFAULT_CUSTOMER_STATUS } from './constants';

/**
 * The wizard's flat-ish react-hook-form shape. Mirrors the legacy
 * `CustomerCreateFormValues` (customer / company / explaination sections) but
 * keeps `company_id` a plain number (Phase-1 defers the async company search /
 * create-company modal to a numeric input). `buildPayload` maps this to the
 * `POST /customers` body verbatim.
 */
export interface CustomerWizardValues {
  customer: {
    customer_type: 'company' | 'individual';
    customer_email: string;
    customer_industry: string;
    customer_source: string;
    customer_source_event: string;
    customer_website: string;
    customer_service_interestings: string[];
    customer_sale_followers: number[];
    customer_source_type: string;
    customer_lead_type: string;
    customer_name: string;
    customer_phone: string;
    customer_position: string;
    customer_dob: string;
    customer_note: string;
  };
  company: {
    company_id: number;
  };
  explaination: {
    explaination_provider: string;
    explaination_budget: number;
    explaination_problem: string;
    explaination_expected: string;
  };
}

/** Initial values. `saleFollowerId` seeds `customer_sale_followers` with the signed-in employee. */
export function defaultValues(saleFollowerId?: number): CustomerWizardValues {
  return {
    customer: {
      customer_type: 'company',
      customer_email: '',
      customer_industry: '',
      customer_source: 'research',
      customer_source_event: '',
      customer_website: '',
      customer_service_interestings: [],
      customer_sale_followers: saleFollowerId ? [saleFollowerId] : [],
      customer_source_type: 'internal',
      customer_lead_type: 'UNCLASSIFIED',
      customer_name: '',
      customer_phone: '',
      customer_position: '',
      customer_dob: '',
      customer_note: '',
    },
    company: { company_id: 0 },
    explaination: {
      explaination_provider: '',
      explaination_budget: 0,
      explaination_problem: '',
      explaination_expected: '',
    },
  };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validation rules lifted from the legacy yup schema. Required: industry,
 * ≥1 interested service, name, valid email, position, phone, ≥1 sale follower,
 * and a positive company_id when the customer is a company.
 */
export const wizardSchema: yup.ObjectSchema<CustomerWizardValues> = yup.object({
  customer: yup.object({
    customer_type: yup
      .mixed<'company' | 'individual'>()
      .oneOf(['company', 'individual'])
      .required(),
    customer_email: yup
      .string()
      .required('Email is required')
      .matches(EMAIL_RE, 'Enter a valid email address'),
    customer_industry: yup.string().required('Select an industry'),
    customer_source: yup.string().required('Select a source'),
    customer_source_event: yup.string().default(''),
    customer_website: yup.string().default(''),
    customer_service_interestings: yup
      .array(yup.string().required())
      .min(1, 'Select at least one service')
      .required(),
    customer_sale_followers: yup
      .array(yup.number().required())
      .min(1, 'Assign at least one sales owner')
      .required(),
    customer_source_type: yup.string().required(),
    customer_lead_type: yup.string().required(),
    customer_name: yup.string().required('Customer name is required'),
    customer_phone: yup.string().required('Phone number is required'),
    customer_position: yup.string().required('Position is required'),
    customer_dob: yup.string().default(''),
    customer_note: yup.string().default(''),
  }),
  company: yup.object({
    company_id: yup.number().defined().default(0),
  }),
  explaination: yup.object({
    explaination_provider: yup.string().default(''),
    explaination_budget: yup.number().min(0).defined().default(0),
    explaination_problem: yup.string().default(''),
    explaination_expected: yup.string().default(''),
  }),
});

/**
 * The react-hook-form field paths validated when leaving each step (0-based).
 * Step 0 (email) is gated by the async existence check, not by these fields.
 */
export function stepFields(step: number, isCompany: boolean): string[] {
  switch (step) {
    case 1:
      return [
        'customer.customer_industry',
        'customer.customer_source',
        'customer.customer_service_interestings',
        'customer.customer_sale_followers',
        ...(isCompany ? ['company.company_id'] : []),
      ];
    case 2:
      return [
        'customer.customer_name',
        'customer.customer_phone',
        'customer.customer_position',
      ];
    default:
      return [];
  }
}

/**
 * Map wizard values → the `POST /customers` payload (§3.1). Nullable date fields
 * (`customer_dob`, `explaination_expected_date`) are carried on the sections'
 * index signatures since the typed shape narrows them to `string | undefined`.
 */
export function buildPayload(v: CustomerWizardValues): CustomerCreatePayload {
  const c = v.customer;
  const industry = c.customer_industry;

  const customer: CustomerCreatePayload['customer'] = {
    customer_type: c.customer_type,
    customer_name: c.customer_name.trim(),
    customer_email: c.customer_email.trim(),
    customer_phone: c.customer_phone.trim(),
    customer_position: c.customer_position.trim(),
    customer_industry: industry,
    customer_source: c.customer_source,
    customer_service_interestings: c.customer_service_interestings,
    customer_sale_followers: c.customer_sale_followers,
    customer_source_type: c.customer_source_type,
    customer_contact_channel: [],
    customer_lead_type: c.customer_lead_type,
    customer_note: c.customer_note.trim(),
    customer_status: DEFAULT_CUSTOMER_STATUS,
  };
  // Nullable in the backend (expects explicit null, not undefined). The typed
  // key narrows to `string | undefined`, so write null via a loose view.
  (customer as Record<string, unknown>).customer_dob = c.customer_dob || null;
  if (c.customer_source_event) customer.customer_source_event = c.customer_source_event.trim();
  if (c.customer_website) customer.customer_website = c.customer_website.trim();

  const explaination: NonNullable<CustomerCreatePayload['explaination']> = {
    explaination_provider: v.explaination.explaination_provider.trim(),
    explaination_budget: v.explaination.explaination_budget || 0,
    explaination_influencer: '',
    explaination_problem: v.explaination.explaination_problem.trim(),
    explaination_expected: v.explaination.explaination_expected.trim(),
    explaination_result: '',
  };
  // Nullable in the backend — write null via a loose view (see above).
  (explaination as Record<string, unknown>).explaination_expected_date = null;

  return {
    customer,
    company: {
      company_id: c.customer_type === 'company' ? v.company.company_id : 0,
      company_industry: industry,
      company_website: c.customer_website.trim(),
    },
    explaination,
  };
}
